import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FCM } from '@capacitor-community/fcm';
import { PushNotifications } from '@capacitor/push-notifications';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { getSupabaseClient } from '../../core/supabase/supabase.client';
import { getFirebaseWebApp } from '../../../firebase-web-init';
import { environment } from '../../../environments/environment';

/**
 * Registers an FCM device token with Supabase (`user_push_devices`) on native iOS/Android and web (PWA).
 * Native uses `@capacitor-community/fcm` (FCM token for HTTP v1). Web uses Firebase JS `getMessaging` + VAPID.
 */
@Injectable({
  providedIn: 'root',
})
export class PushDeviceService {
  private supabase = getSupabaseClient();
  private currentUserId: string | null = null;
  private listenersAttached = false;
  private readonly webMessagingSwPath = '/firebase-messaging-sw.js';
  private readonly legacyWebMessagingSwScope = '/firebase-cloud-messaging-push-scope';

  clearUser(): void {
    this.currentUserId = null;
  }

  /**
   * Called after `public.users` is loaded.
   * Registers FCM token in `user_push_devices` (native or web).
   */
  async syncAfterLogin(userId: string): Promise<void> {
    this.currentUserId = userId;
    if (Capacitor.isNativePlatform()) {
      await this.syncNativePush();
    } else {
      await this.syncWebPush();
    }
  }

  private async syncNativePush(): Promise<void> {
    await this.attachListenersOnce();
    try {
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'prompt') {
        perm = await PushNotifications.requestPermissions();
      }
      if (perm.receive !== 'granted') {
        return;
      }
      await PushNotifications.register();
      await this.refreshAndPersistFcmTokenNative();
    } catch (e) {
      console.warn('Push registration skipped:', e);
    }
  }

  private async syncWebPush(): Promise<void> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    const cfg = environment.firebaseWeb;
    const vapidKey = cfg?.webPushVapidKey?.trim();
    if (!cfg || !vapidKey) {
      return;
    }
    const app = getFirebaseWebApp();
    if (!app) {
      return;
    }
    if (!(await isSupported())) {
      return;
    }
    try {
      if (!('Notification' in window)) {
        return;
      }
      if (Notification.permission === 'denied') {
        return;
      }
      if (Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        if (result !== 'granted') {
          return;
        }
      }
      const messaging = getMessaging(app);
      const registration = await this.getStableWebMessagingRegistration();
      if (!registration || !('pushManager' in registration)) {
        console.warn('FCM web push skipped: invalid service worker registration');
        return;
      }
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (token) {
        await this.persistToken(token);
      }
    } catch (e) {
      console.warn('Web push registration skipped:', e);
    }
  }

  private async getStableWebMessagingRegistration(): Promise<ServiceWorkerRegistration | null> {
    // Remove legacy custom-scope registration used by older builds to avoid token-manager mismatch.
    const legacyReg = await navigator.serviceWorker.getRegistration(this.legacyWebMessagingSwScope);
    if (legacyReg) {
      await legacyReg.unregister();
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    const existing = registrations.find((reg) =>
      reg.active?.scriptURL?.endsWith(this.webMessagingSwPath) ||
      reg.waiting?.scriptURL?.endsWith(this.webMessagingSwPath) ||
      reg.installing?.scriptURL?.endsWith(this.webMessagingSwPath)
    );

    if (existing) {
      await existing.update().catch(() => undefined);
      return existing;
    }

    const registration = await navigator.serviceWorker.register(this.webMessagingSwPath);
    await navigator.serviceWorker.ready;
    return registration;
  }

  private async attachListenersOnce(): Promise<void> {
    if (this.listenersAttached) {
      return;
    }
    this.listenersAttached = true;
    await PushNotifications.addListener('registration', () => {
      void this.refreshAndPersistFcmTokenNative();
    });
    await PushNotifications.addListener('registrationError', (err) => {
      console.warn('Push registrationError', err);
    });
  }

  private async refreshAndPersistFcmTokenNative(): Promise<void> {
    try {
      const { token } = await FCM.getToken();
      if (token) {
        await this.persistToken(token);
      }
    } catch (e) {
      console.warn('FCM.getToken failed', e);
    }
  }

  private async persistToken(token: string): Promise<void> {
    const userId = this.currentUserId;
    if (!userId || !token) {
      return;
    }
    const cap = Capacitor.getPlatform();
    const platform = cap === 'ios' ? 'ios' : cap === 'android' ? 'android' : 'web';

    const { error: rpcErr } = await this.supabase.rpc('register_push_device', {
      p_device_token: token,
      p_platform: platform,
    });
    if (!rpcErr) {
      return;
    }

    console.warn('register_push_device RPC failed, using fallback upsert', rpcErr);
    await this.persistTokenFallbackUpsert(userId, token, platform);
  }

  /** Legacy path if migration not applied yet: upsert then deactivate other tokens for this user. */
  private async persistTokenFallbackUpsert(
    userId: string,
    token: string,
    platform: 'ios' | 'android' | 'web',
  ): Promise<void> {
    const now = new Date().toISOString();
    const { error: upErr } = await this.supabase.from('user_push_devices').upsert(
      {
        user_id: userId,
        device_token: token,
        platform,
        is_active: true,
        last_seen_at: now,
        updated_at: now,
      },
      { onConflict: 'device_token' },
    );
    if (upErr) {
      console.warn('user_push_devices upsert failed', upErr);
      return;
    }
    const { error: deactErr } = await this.supabase
      .from('user_push_devices')
      .update({ is_active: false, updated_at: now })
      .eq('user_id', userId)
      .neq('device_token', token);
    if (deactErr) {
      console.warn('user_push_devices deactivate others failed', deactErr);
    }
  }

  /**
   * Marks all push tokens for the current auth user inactive (call before clearing session on logout).
   */
  async deactivateAllPushDevices(): Promise<void> {
    const { error } = await this.supabase.rpc('deactivate_all_push_devices');
    if (error) {
      console.warn('deactivate_all_push_devices failed', error);
      await this.deactivateAllPushDevicesFallback();
    }
  }

  private async deactivateAllPushDevicesFallback(): Promise<void> {
    const uid = this.currentUserId;
    if (!uid) {
      return;
    }
    const now = new Date().toISOString();
    const { error } = await this.supabase
      .from('user_push_devices')
      .update({ is_active: false, updated_at: now })
      .eq('user_id', uid);
    if (error) {
      console.warn('user_push_devices logout deactivate failed', error);
    }
  }
}
